import { z } from 'zod';
import { STRIPE_SCHLUESSEL } from './app';
import type { Branding } from './branding';

/* ------------------------------------------------------------------ DXL-App (Plattform) */

/** Eingabe: Standard-Adresse der App und DXL-Admin-Token. Leeres Token = unverändert. */
export const PlattformAppSchema = z.object({
  url: z
    .string()
    .trim()
    .max(300, 'Höchstens 300 Zeichen')
    .regex(/^https:\/\/[^\s/]+(\/[^\s]*)?$/, 'Bitte die Adresse der App mit https:// eintragen.')
    .transform((v) => v.replace(/\/+$/, '')),
  adminToken: z
    .string()
    .trim()
    .max(300, 'Höchstens 300 Zeichen')
    .refine((v) => v === '' || v.length >= 16, 'Das Admin-Token hat mindestens 16 Zeichen.')
    .default(''),
  adminTokenLoeschen: z.boolean().default(false),
});
export type PlattformAppEingabe = z.input<typeof PlattformAppSchema>;

export interface PlattformApp {
  url: string;
  /** Vorgabe ohne Einstellung */
  standard: string;
  adminTokenGesetzt: boolean;
  /** Basisadresse dieser Plattform – je Marktplatz `${basis}/m/<kürzel>` */
  plattformBasis: string;
  geaendertAm: string | null;
}

export type PlattformAppGesundheit = { ok: true; ms: number; zeit: string } | { ok: false; fehler: string };

/**
 * Abgleich Verzeichnis der App ↔ Marktplätze der Plattform:
 * - aktuell: Kürzel zeigt auf diese Plattform
 * - umstellen: Kürzel zeigt auf eine andere Adresse (meist die PHP-Middleware) – Umstellen nur nach Bestätigung
 * - fehlt: Marktplatz der Plattform ist in der App nicht eingetragen
 * - nur_app: Eintrag der App ohne Marktplatz auf dieser Plattform
 */
export type VerzeichnisZustand = 'aktuell' | 'umstellen' | 'fehlt' | 'nur_app';

export interface VerzeichnisZeile {
  kuerzel: string;
  mandantId: string | null;
  mandantName: string | null;
  /** Eintrag in der App */
  appName: string | null;
  appUrl: string | null;
  aktiv: boolean | null;
  /** Adresse auf dieser Plattform */
  sollUrl: string | null;
  /** Eintrag zeigt auf die PHP-Middleware */
  php: boolean;
  zustand: VerzeichnisZustand;
}

export interface Verzeichnis {
  zeilen: VerzeichnisZeile[];
  geladenAm: string;
}

export const VerzeichnisRegistrierenSchema = z.object({
  /** Kürzel, die von einer anderen Adresse auf diese Plattform umgestellt werden dürfen (Bestätigung im Dialog) */
  umstellen: z.array(z.string().trim().toUpperCase().regex(/^[A-Z0-9]{2,10}$/)).max(1000).default([]),
});
export type VerzeichnisRegistrierenEingabe = z.input<typeof VerzeichnisRegistrierenSchema>;

export type RegistrierErgebnisArt = 'registriert' | 'aktualisiert' | 'umgestellt' | 'bestaetigung' | 'fehler';

export interface RegistrierErgebnis {
  kuerzel: string;
  name: string;
  ergebnis: RegistrierErgebnisArt;
  fehler?: string;
}

export interface VerzeichnisRegistrierung {
  ergebnisse: RegistrierErgebnis[];
  /** Abgleich nach der Registrierung – null ohne Admin-Token */
  verzeichnis: Verzeichnis | null;
}

/* ------------------------------------------------------------------ Stripe (Plattform) */

export const STRIPE_WEBHOOK_SECRET = /^whsec_[A-Za-z0-9+/=]{10,}$/;
export const STRIPE_PUBLISHABLE = /^pk_(test|live)_[A-Za-z0-9]{10,}$/;

/**
 * Secret Key und Webhook-Secret: leer = unverändert (sie werden nie angezeigt).
 * Publishable Key ist öffentlich und wird angezeigt: fehlt = unverändert, leer = entfernen.
 */
export const PlattformStripeSchema = z.object({
  secretKey: z.string().trim().refine((v) => v === '' || STRIPE_SCHLUESSEL.test(v), 'Bitte den geheimen Schlüssel (sk_live_… oder sk_test_…) eintragen.').default(''),
  webhookSecret: z.string().trim().refine((v) => v === '' || STRIPE_WEBHOOK_SECRET.test(v), 'Das Webhook-Secret beginnt mit whsec_.').default(''),
  publishableKey: z.string().trim().refine((v) => v === '' || STRIPE_PUBLISHABLE.test(v), 'Der Publishable Key beginnt mit pk_live_ oder pk_test_.').optional(),
});
export type PlattformStripeEingabe = z.input<typeof PlattformStripeSchema>;

export interface PlattformStripe {
  /** Woher der wirksame Secret Key kommt */
  quelle: 'einstellung' | 'umgebung' | null;
  modus: 'test' | 'live' | null;
  /** nur die Art, z. B. „sk_test_…“ – nie der Schlüssel */
  secretArt: string;
  secretGesetzt: boolean;
  webhookGesetzt: boolean;
  publishableKey: string;
  geprueftAm: string | null;
  geaendertAm: string | null;
  /** Was als Umgebungsvariable gesetzt ist (Rückfall) */
  umgebung: { secretKey: boolean; webhookSecret: boolean; publishableKey: boolean };
}

export type PlattformStripeTest = { ok: true; modus: 'test' | 'live'; quelle: 'einstellung' | 'umgebung' } | { ok: false; fehler: string };

/* ------------------------------------------------------------------ Branding (Plattform) */

export interface PlattformBrandingInfo {
  /** true, wenn DXL ein eigenes Plattform-Branding gespeichert hat */
  eigen: boolean;
  branding: Branding;
}
